import { BillingPostageType, PaymentOptions, PurchasePostageEnum, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import { defaultCountry } from '@app/constants/index';
import { IAccount, IAutoBuyFields, IBillingBalance } from '@app/models/index';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { MetaDataService } from '@app/services/meta-data.service';
import { SharedService } from '@app/services/shared.service';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CircularProgress,
	Grid,
	InputAdornment,
	Link,
	Switch,
	TextField,
	Typography,
} from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';
import PaymentMethodComponent from './payment-method.component';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const metaDataService: MetaDataService = Injector.get(MetaDataService);
const sharedServive: SharedService = Injector.get(SharedService);
const PHAmountMinLimit = 1;
const PHAutoBuyMinLimit = 1;
const PHAutoBuyThreshold = 1;

class PurchasePostageComponent extends React.Component<models.IAccountPostageProps, models.IAccountPostageState> {
	private userData: models.IUserSignInResponse;
	private accountBalance: models.IAccountBalance;
	private AutoBuyFactory: IAutoBuyFields;
	private PurchasePostageFactory: models.IPurchasePostageFields;
	private currentBalance: number = 0;
	private paymentDetailsAuxiliary: models.IPaymentValueSetUp;
	private PurchasePostageSchema: any = Yup.object().shape<models.IPurchasePostageFields>({
		amount: Yup.number()
			.required(intlService.GetFormattedMessage('app.billing.purchase-postage-amount-required-validation'))
			.min(1, intlService.GetFormattedMessage('app.billing.purchase-postage-amount-min-length-validation') + 1),
	});

	private AutoBuySchema: any = Yup.object().shape<IAutoBuyFields>({
		autoBuyAmount: Yup.number()
			.required(intlService.GetFormattedMessage('app.billing.auto-buy-amount-required-validation'))
			.min(1, intlService.GetFormattedMessage('app.billing.auto-buy-amount-min-length-validation') + 1),
		autoBuyThreshold: Yup.number()
			.required(intlService.GetFormattedMessage('app.billing.auto-buy-threshold-required-validation'))
			.min(1, intlService.GetFormattedMessage('app.billing.auto-buy-threshold-min-length-validation') + 1),
		isAutoBuy: Yup.boolean().required(),
	});

	constructor(props: models.IAccountPostageProps) {
		super(props);

		this.state = models.initialAccountPostage();
		this.userData = accountService.getUserData();
		this.accountBalance = {
			autoBuyAmount: 0,
			autoBuyThreshold: 0,
			balance: 0,
			isAutoBuy: false,
		};
		this.AutoBuyFactory = {
			autoBuyAmount: 0,
			autoBuyThreshold: 0,
			isAutoBuy: false,
		};
		this.PurchasePostageFactory = {
			amount: 0,
		};
	}

	public componentDidMount() {
		// get current account details
		this.getAccountDetails();
		this.getAllCountries();
		// Get States for US
		this.getStatesForCountry();
	}

	public render() {
		return (
			<div>
				{/* Purchase Postage Section*/}
				<Grid container={true} direction='column' spacing={2}>
					<Grid item={true} lg={12}>
						<Formik
							initialValues={this.PurchasePostageFactory}
							enableReinitialize={true}
							onSubmit={(e) =>
								this.openDialog(
									BillingPostageType.PurchasePostage,
									this.props.intl.formatMessage({
										id: 'app.billing.purchase-postage-label',
									}),
									this.props.intl.formatMessage({
										id: 'app.billing.purchase-confirm-dialog-text',
									}),
									this.props.intl.formatMessage({
										id: 'app.billing.purchase-confirm-dialog-purchase-button-text',
									}),
									this.props.intl.formatMessage({
										id: 'app.common.dialog-cancel-button-text',
									}),
									e,
									PurchasePostageEnum.AddBalance
								)
							}
							validationSchema={this.PurchasePostageSchema}
							render={(props: FormikProps<FormikValues>) => (
								<Form noValidate={true}>
									<Accordion
										defaultExpanded={history.location.state?.extraParams?.isRoutedFrom !== 'ProfileCompleteness'}
									>
										<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
											<Grid container={true} direction='row' justifyContent='space-evenly'>
												<Grid item={true} xs={12}>
													<Typography variant='h6' color='inherit'>
														{this.props.intl.formatMessage({
															id: 'app.billing.purchase-postage-label',
														})}
													</Typography>
												</Grid>
											</Grid>
										</AccordionSummary>
										<AccordionDetails>
											<Grid container={true} direction='row' className='text-center'>
												<Grid item={true} xs={12}>
													<Typography variant='subtitle2' color='inherit'>
														{this.props.intl.formatMessage({
															id: 'app.billing.purchase-current-balance-label',
														})}{' '}
														:{' '}
														{this.state.dataLoading ? (
															<span className='pl-1 circular-progress-position'>
																<CircularProgress size={14} />
															</span>
														) : (
															<span className='primary-color' id='currentBalance'>
																$ {this.currentBalance}
															</span>
														)}
													</Typography>
												</Grid>
												<Grid item={true} xs={12}>
													<Field
														name='amount'
														label={this.props.intl.formatMessage({
															id: 'app.billing.purchase-amount-label',
														})}
														margin='normal'
														variant='outlined'
														component={TextField}
														type='number'
														value={props.values.amount}
														required={true}
														onChange={(e: React.ChangeEvent) => props.handleChange('amount')(e)}
														error={!!props.errors.amount}
														helperText={props.errors.amount}
														inputProps={{
															'data-testid': 'amount',
															min: PHAmountMinLimit,
														}}
														InputProps={{
															startAdornment: <InputAdornment position='start'>$</InputAdornment>,
														}}
													/>
												</Grid>
												<Grid item={true} xs={12} className='mt-2 mb-2'>
													{/* Button component */}
													<CustomButton
														disabled={this.state.postageloading || !!Object.keys(props.errors).length}
														buttonText={this.props.intl.formatMessage({
															id: 'app.billing.purchase-button-text',
														})}
														loading={this.state.postageloading}
													/>
												</Grid>
												<Grid item={true} xs={12}>
													{this.props.intl.formatMessage({
														id: 'app.billing.payment-method-charge-amount-label',
													})}
												</Grid>
												<Grid item={true} xs={12}>
													<Link
														component='button'
														onClick={() => {
															history.push(models.Module.Reports.children.ReportAccountStatement.url);
														}}
													>
														{intlService.GetFormattedMessage('app.billing.view-report')}
														{models.Module.Reports.children.ReportAccountStatement.toString()}
													</Link>
												</Grid>
											</Grid>
										</AccordionDetails>
									</Accordion>
								</Form>
							)}
						/>
					</Grid>
				</Grid>

				{/* Auto Buy Section */}
				<Grid container={true} direction='column' spacing={2}>
					<Grid item={true} lg={12}>
						<Accordion>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Grid container={true} direction='row' justifyContent='space-around'>
									<Grid item={true} xs={12}>
										<Typography variant='h6' color='inherit'>
											{this.props.intl.formatMessage({
												id: 'app.billing.auto-buy-label',
											})}
										</Typography>
									</Grid>
								</Grid>
							</AccordionSummary>
							<AccordionDetails>
								<Formik
									initialValues={this.AutoBuyFactory}
									enableReinitialize={true}
									onSubmit={(e) =>
										this.openDialog(
											BillingPostageType.AutoBuy,
											this.props.intl.formatMessage({
												id: 'app.billing.auto-buy-label',
											}),
											this.props.intl.formatMessage({
												id: 'app.billing.auto-buy-confirm-dialog-text',
											}),
											this.props.intl.formatMessage({
												id: 'app.billing.auto-buy-confirm-dialog-confirm-button-text',
											}),
											this.props.intl.formatMessage({
												id: 'app.common.dialog-cancel-button-text',
											}),
											e,
											PurchasePostageEnum.AutoBuy
										)
									}
									validationSchema={this.AutoBuySchema}
									render={(props: FormikProps<FormikValues>) => (
										<Form noValidate={true} className='width-100'>
											<Grid item={true} xs={12}>
												<Typography variant='subtitle1' color='inherit' align='right'>
													{props.values.isAutoBuy
														? this.props.intl.formatMessage({
																id: 'app.billing.auto-buy-toggle-text-on',
														  })
														: this.props.intl.formatMessage({
																id: 'app.billing.auto-buy-toggle-text-off',
														  })}

													<Field
														name='isAutoBuy'
														margin='normal'
														render={() => (
															<Switch
																onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																	props.handleChange('isAutoBuy')(e);
																	props.values.isAutoBuy = e.target.checked ? true : false;
																	this.openDialog(
																		BillingPostageType.AutoBuy,
																		this.props.intl.formatMessage({
																			id: 'app.billing.auto-buy-label',
																		}),
																		this.props.intl.formatMessage({
																			id: 'app.billing.auto-buy-confirm-dialog-text',
																		}),
																		this.props.intl.formatMessage({
																			id: 'app.billing.auto-buy-confirm-dialog-confirm-button-text',
																		}),
																		this.props.intl.formatMessage({
																			id: 'app.billing.auto-buy-confirm-dialog-cancel-button-text',
																		}),
																		props.values,
																		PurchasePostageEnum.AutoBuy
																	);
																}}
																color='primary'
																disabled={!!Object.keys(props.errors).length}
																checked={props.values.isAutoBuy}
															/>
														)}
													/>
												</Typography>
											</Grid>
											<Grid container={true} direction='row' alignContent='center' justifyContent='space-evenly'>
												<Grid item={true} xs={5}>
													<Field
														name='autoBuyAmount'
														label={this.props.intl.formatMessage({
															id: 'app.billing.auto-buy-amount-label',
														})}
														margin='normal'
														variant='outlined'
														component={TextField}
														type='number'
														value={props.values.autoBuyAmount}
														disabled={this.state.autoBuyloading || !props.values.isAutoBuy}
														required={true}
														onChange={(e: React.ChangeEvent) => props.handleChange('autoBuyAmount')(e)}
														error={!!props.errors.autoBuyAmount}
														helperText={props.errors.autoBuyAmount}
														inputProps={{
															'data-testid': 'autoBuyAmount',
															min: PHAutoBuyMinLimit,
														}}
														InputProps={{
															startAdornment: <InputAdornment position='start'>$</InputAdornment>,
														}}
													/>
												</Grid>
												<Grid item={true} xs={5}>
													<Field
														name='autoBuyThreshold'
														margin='normal'
														label={this.props.intl.formatMessage({
															id: 'app.billing.auto-buy-when-balance-reaches-label',
														})}
														variant='outlined'
														component={TextField}
														type='number'
														value={props.values.autoBuyThreshold}
														disabled={this.state.autoBuyloading || !props.values.isAutoBuy}
														required={true}
														onChange={(e: React.ChangeEvent) => props.handleChange('autoBuyThreshold')(e)}
														error={!!props.errors.autoBuyThreshold}
														helperText={props.errors.autoBuyThreshold}
														inputProps={{
															'data-testid': 'autoBuyThreshold',
															min: PHAutoBuyThreshold,
														}}
														InputProps={{
															startAdornment: <InputAdornment position='start'>$</InputAdornment>,
														}}
													/>
												</Grid>
												<Grid item={true} xs={12} className='text-center mt-2 mb-2'>
													{/* Button component */}
													<CustomButton
														disabled={this.state.autoBuyloading || !!Object.keys(props.errors).length}
														buttonText={this.props.intl.formatMessage({
															id: 'app.billing.auto-buy-update-button-text',
														})}
														loading={this.state.autoBuyloading}
													/>
												</Grid>
											</Grid>
										</Form>
									)}
								/>
							</AccordionDetails>
						</Accordion>
					</Grid>
				</Grid>

				{/* Payment Method panel */}
				<Grid container={true} direction='column' spacing={2}>
					<Grid item={true} lg={12}>
						<Accordion defaultExpanded={history.location.state?.extraParams?.isRoutedFrom === 'ProfileCompleteness'}>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Grid container={true} direction='row' justifyContent='space-evenly'>
									<Grid item={true} xs={12}>
										<Typography variant='h6' color='inherit'>
											{this.props.intl.formatMessage({
												id: 'app.billing.payment-method-label',
											})}{' '}
											{this.state.dataLoading ? (
												<span className='pl-1 circular-progress-position'>
													<CircularProgress size={22} />
												</span>
											) : !this.state.isUpdate ? (
												`- ${this.state.defaultPayment}`
											) : null}
										</Typography>
									</Grid>
								</Grid>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container={true} justifyContent='center'>
									<Grid item={true} xs={12}>
										<PaymentMethodComponent
											paymentMethodData={{
												isUpdate: this.state.isUpdate,
												prefilledData: this.paymentDetailsAuxiliary,
												onUpdateCb: (value: boolean, paymentMethod?: PaymentOptions) => {
													this.setState({
														isUpdate: value,
													});
													if (paymentMethod) {
														this.setState({
															defaultPayment: paymentMethod,
														});
													}
												},
												defaultMethod: this.state.defaultPayment,
											}}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>
					</Grid>
				</Grid>

				{/* // Dialog Component */}
				<CustomDialogBox
					open={this.state.openDialog}
					dialogTitle={this.state.dialogTitle}
					dialogDescription={this.state.dialogDescription}
					dialogNoButton={this.state.dialogNoButton}
					dialogYesButton={this.state.dialogYesButton}
					closeCustomDialog={this.closeDialog}
					dialogId={this.state.dialogCheck}
				/>
			</div>
		);
	}

	// open  Confirm dialog
	public openDialog = (
		dialogPannel: string,
		title: string,
		description: string,
		yesButton: string,
		noButton: string,
		eventData: any,
		dialogCheck: PurchasePostageEnum
	) => {
		this.UpdateFormValues(dialogPannel, eventData);
		this.setState({
			autoBuyloading: dialogPannel === BillingPostageType.PurchasePostage ? false : true,
			dialogDescription: description,
			dialogNoButton: noButton,
			dialogTitle: title,
			dialogYesButton: yesButton,
			openDialog: true,
			open: false,
			postageloading: dialogPannel === BillingPostageType.PurchasePostage ? true : false,
			dialogCheck,
		});
	};

	// close dialog box
	public closeDialog = (dialogPannel?: string, isCancel?: boolean) => {
		this.setState({ openDialog: false });
		if (!isCancel) {
			this.updateAccountBalance(dialogPannel);
		} else {
			this.AutoBuyFactory = {
				autoBuyAmount: this.accountBalance.autoBuyAmount,
				autoBuyThreshold: this.accountBalance.autoBuyThreshold,
				isAutoBuy: this.accountBalance.isAutoBuy,
			};
			this.PurchasePostageFactory = {
				amount: 0,
			};
			this.setState({ autoBuyloading: false, postageloading: false });
		}
	};
	// open  snackbar
	public openSnackbar = (message: string | string[], variantName: SnackBarVariants) => {
		this.setState({
			autoBuyloading: false,
			postageloading: false,
			paymentMethodLoading: false,
		});
		this.props.openSnackBar(message, variantName);
	};

	// update account details
	public async updateAccountBalance(dialogPanel: string) {
		try {
			let result: IAccount;
			if (dialogPanel === PurchasePostageEnum.AutoBuy) {
				result = await accountService.updateAccountAutoBuyDetail(
					this.AutoBuyFactory,
					this.props.axiosCancellationToken
				);
			} else {
				const addBalance: IBillingBalance = { balance: 0 };
				addBalance.balance = this.PurchasePostageFactory.amount;
				result = await accountService.updateAccountBalance(addBalance, this.props.axiosCancellationToken);
			}
			this.PurchasePostageFactory = {
				amount: this.state.defaultPostageAmount,
			};
			this.userData.account = { ...this.userData.account, ...result };
			this.accountBalance = {
				autoBuyAmount: result.autoBuyAmount,
				autoBuyThreshold: result.autoBuyThreshold,
				balance: result.balance ? result.balance : this.currentBalance,
				isAutoBuy: result.isAutoBuy,
			};
			this.currentBalance = result.balance ? result.balance : this.currentBalance;
			accountService.setUserData(this.userData);
			this.setState({ autoBuyloading: false, postageloading: false });
			this.openSnackbar(
				this.props.intl.formatMessage({
					id:
						dialogPanel === PurchasePostageEnum.AutoBuy
							? 'app.billing.auto-buy-updated-successfully'
							: 'app.billing.purchase-postage-successfully',
				}),
				SnackBarVariants.Success
			);
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	// update form values
	private UpdateFormValues(dialogPannel: string, eventData: any) {
		if (dialogPannel === BillingPostageType.AutoBuy) {
			this.AutoBuyFactory.autoBuyAmount = eventData.autoBuyAmount;
			this.AutoBuyFactory.autoBuyThreshold = eventData.autoBuyThreshold;
			this.AutoBuyFactory.isAutoBuy = eventData.isAutoBuy;
		} else {
			this.PurchasePostageFactory.amount = eventData.amount;
		}
	}

	// get current account details
	private async getAccountDetails() {
		try {
			const result = await accountService.getAccountDetails(this.props.axiosCancellationToken);
			this.accountBalance = {
				autoBuyAmount: result.autoBuyAmount,
				autoBuyThreshold: result.autoBuyThreshold ? result.autoBuyThreshold : 0,
				balance: result.balance ? result.balance : 0,
				isAutoBuy: result.isAutoBuy ? result.isAutoBuy : false,
			};
			this.AutoBuyFactory = {
				autoBuyAmount: this.accountBalance.autoBuyAmount,
				autoBuyThreshold: this.accountBalance.autoBuyThreshold,
				isAutoBuy: this.accountBalance.isAutoBuy,
			};
			this.PurchasePostageFactory = {
				amount: result.minLimit,
			};
			this.currentBalance = result.balance;
			const defaultPaymentCheck = result?.creditCardPaymentAccountResponse?.default
				? PaymentOptions.CreditCard
				: result?.achPaymentAccountResponse?.default
				? PaymentOptions.ACH
				: PaymentOptions.None;

			this.setState({
				postageloading: false,
				defaultPayment: defaultPaymentCheck,
				paymentOptionValue: defaultPaymentCheck,
			});

			// Setting Payment Form
			if (result.creditCardPaymentAccountResponse) {
				this.paymentDetailsAuxiliary = {
					cardHolderName: result.creditCardPaymentAccountResponse.cardHolderName,
					expirationMonth: result.creditCardPaymentAccountResponse.expirationMonth,
					expirationYear: result.creditCardPaymentAccountResponse.expirationYear,
					lastFourCredit: result.creditCardPaymentAccountResponse.lastFour,
				};
			}
			if (result.achPaymentAccountResponse) {
				this.paymentDetailsAuxiliary = {
					...this.paymentDetailsAuxiliary,
					accountHolderName: result.achPaymentAccountResponse.accountHolderName,
					lastFourAccount: result.achPaymentAccountResponse.lastFour,
					routingNumber: result.achPaymentAccountResponse.routingNumber,
				};
			}

			this.setState({
				dataLoading: false,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private async getAllCountries() {
		try {
			const result = await metaDataService.getAllCountries(this.props.axiosCancellationToken);
			this.setState({ countries: result });
		} catch (error) {
			if (!Axios.isCancel(error)) {
				throw error;
			}
		}
	}

	private async getStatesForCountry() {
		try {
			const result = await metaDataService.getStates(defaultCountry, this.props.axiosCancellationToken);
			this.setState({ states: result });
		} catch (error) {
			if (!Axios.isCancel(error)) {
				throw error;
			}
		}
	}
}

export default withAxiosCancellation(withSnackBar(injectIntl(PurchasePostageComponent)));



// WEBPACK FOOTER //
// ./src/components/account/purchase-postage/purchase-postage.component.tsx